import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { AlertTriangle, X, Zap } from 'lucide-react';

/**
 * QuotaWarningBanner — Sticky banner at top of Dashboard when user is near
 * or at daily/monthly quota limits. Auto-dismissable per session.
 *
 * Threshold behavior:
 *  - 80-94%  : amber warning (dismissable)
 *  - 95-99%  : red warning (dismissable)
 *  - 100%+   : red solid, NOT dismissable (blocked)
 */
export const QuotaWarningBanner = ({ data: propData }) => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(propData || null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (propData) {
      setData(propData);
      return;
    }
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/plan/limits`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
        setData(res.data);
      } catch {
        /* silent fail */
      }
    };
    if (token) {
      fetchData();
      const interval = setInterval(fetchData, 60000); // refresh every 1 min
      return () => clearInterval(interval);
    }
  }, [token, propData]);

  useEffect(() => {
    // Read dismissed state per-day from localStorage
    const todayKey = new Date().toISOString().slice(0, 10);
    const stored = localStorage.getItem('quota_banner_dismissed');
    if (stored === todayKey) setDismissed(true);
  }, []);

  const handleDismiss = () => {
    const todayKey = new Date().toISOString().slice(0, 10);
    localStorage.setItem('quota_banner_dismissed', todayKey);
    setDismissed(true);
  };

  if (!data || dismissed) return null;

  const { plan, usage } = data;
  const monthlyPct = usage.monthly_limit > 0 ? (usage.monthly_used / usage.monthly_limit) * 100 : 0;
  const dailyPct = usage.daily_limit > 0 ? (usage.daily_used / usage.daily_limit) * 100 : 0;
  const maxPct = Math.max(monthlyPct, dailyPct);

  // Only show if ≥80%
  if (maxPct < 80) return null;

  const isBlocked = maxPct >= 100;
  const isCritical = maxPct >= 95;
  const limitType = dailyPct >= monthlyPct ? 'harian' : 'bulanan';
  const usedCount = limitType === 'harian' ? usage.daily_used : usage.monthly_used;
  const limitCount = limitType === 'harian' ? usage.daily_limit : usage.monthly_limit;

  const bgColor = isBlocked || isCritical
    ? 'bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-800'
    : 'bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800';
  const textColor = isBlocked || isCritical
    ? 'text-red-800 dark:text-red-200'
    : 'text-amber-800 dark:text-amber-200';
  const iconColor = isBlocked || isCritical
    ? 'text-red-600 dark:text-red-400'
    : 'text-amber-600 dark:text-amber-400';

  return (
    <div
      className={`rounded-lg border px-4 py-3 ${bgColor} flex items-start gap-3`}
      data-testid="quota-warning-banner"
    >
      {isBlocked ? (
        <Zap className={`w-5 h-5 ${iconColor} flex-shrink-0 mt-0.5`} />
      ) : (
        <AlertTriangle className={`w-5 h-5 ${iconColor} flex-shrink-0 mt-0.5`} />
      )}
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${textColor}`} data-testid="quota-warning-title">
          {isBlocked
            ? `Kuota ${limitType} habis — AI sementara non-aktif`
            : `Kuota ${limitType} hampir habis (${maxPct.toFixed(0)}%)`}
        </p>
        <p className={`text-xs mt-0.5 ${textColor} opacity-85`}>
          Terpakai {usedCount.toLocaleString('id-ID')} dari {limitCount.toLocaleString('id-ID')} pesan {limitType === 'harian' ? 'hari ini' : 'bulan ini'}.
          {isBlocked
            ? ` AI akan aktif lagi setelah reset ${limitType === 'harian' ? '00:00 UTC' : 'siklus bulanan'}.`
            : plan === 'business' || plan === 'enterprise'
              ? ''
              : ' Upgrade plan untuk kuota lebih besar.'}
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {plan !== 'business' && plan !== 'enterprise' && (
          <button
            onClick={() => navigate('/pricing')}
            className={`text-xs font-semibold px-3 py-1.5 rounded-md ${
              isBlocked || isCritical
                ? 'bg-red-600 hover:bg-red-700 text-white'
                : 'bg-amber-600 hover:bg-amber-700 text-white'
            } transition-colors`}
            data-testid="quota-warning-upgrade-btn"
          >
            Upgrade
          </button>
        )}
        {!isBlocked && (
          <button
            onClick={handleDismiss}
            className={`p-1 rounded-md hover:bg-black/5 dark:hover:bg-white/5 ${textColor} dark:bg-zinc-900`}
            aria-label="Dismiss"
            data-testid="quota-warning-dismiss-btn"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default QuotaWarningBanner;
